import {
    ComponentPropsWithoutRef,
    ForwardedRef,
    createContext,
    forwardRef,
    useContext,
    useMemo,
    useState,
} from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Box, BoxProps } from '@givebutter/primitives/box';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { maskDecimal } from '@givebutter/util';
import {
    faArrowPointer,
    faBellSlash,
    faEnvelopeOpen,
    faMessagesDollar,
    faMobile,
    faPercent,
    faScaleUnbalancedFlip,
    faXmarkToSlot,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { fetchWorkflowMetrics } from 'api/workflows';
import { WorkflowActionTypeEnum } from 'constants/workflows';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { workflowQueryKeys } from 'queries/workflow-query-keys';
import { twMerge } from 'tailwind-merge';

type MetricType =
    | 'total_revenue_from_messages'
    | 'average_revenue'
    | 'conversion_rate'
    | 'unsubscribe_rate'
    | 'email_bounce_rate'
    | 'email_unique_open_rate'
    | 'sms_delivery_rate'
    | 'click_through_rate';

interface IMetricCardContext {
    selectedOption: MetricType;
    setSelectedOption: (selected: MetricType) => void;
    messageTypes: string[];
}

const MetricCardContext = createContext<IMetricCardContext>({
    selectedOption: '' as MetricType,
    setSelectedOption: () => {},
    messageTypes: [],
});

interface MetricCardRootProps extends BoxProps {
    initialOption: IMetricCardContext['selectedOption'];
    messageTypes: IMetricCardContext['messageTypes'];
}
const MetricCardRoot = forwardRef(
    (
        { initialOption, messageTypes, className, ...rest }: MetricCardRootProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const [selectedOption, setSelectedOption] = useState(initialOption);
        const providerValue = useMemo(
            () => ({ selectedOption, setSelectedOption, messageTypes }),
            [selectedOption, setSelectedOption, messageTypes],
        );
        return (
            <MetricCardContext.Provider value={providerValue}>
                <Box
                    className={twMerge('flex flex-col gap-4 overflow-hidden p-4', className)}
                    ref={forwardedRef}
                    {...rest}
                />
            </MetricCardContext.Provider>
        );
    },
);

interface MetricCardHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const MetricCardHeader = forwardRef(
    ({ className, ...rest }: MetricCardHeaderProps, forwardedRef: ForwardedRef<HTMLDivElement>) => (
        <div
            className={twMerge('flex items-center gap-2', className)}
            ref={forwardedRef}
            {...rest}
        />
    ),
);

interface MetricCardIconProps extends Omit<FontAwesomeIconProps, 'icon'> {}
const MetricCardIcon = forwardRef(
    ({ className, ...rest }: MetricCardIconProps, forwardedRef: ForwardedRef<SVGSVGElement>) => {
        const { selectedOption } = useContext(MetricCardContext);

        const icon = useMemo(() => {
            switch (selectedOption) {
                case 'total_revenue_from_messages':
                    return faMessagesDollar;
                case 'average_revenue':
                    return faScaleUnbalancedFlip;
                case 'conversion_rate':
                    return faPercent;
                case 'unsubscribe_rate':
                    return faBellSlash;
                case 'email_bounce_rate':
                    return faXmarkToSlot;
                case 'email_unique_open_rate':
                    return faEnvelopeOpen;
                case 'sms_delivery_rate':
                    return faMobile;
                case 'click_through_rate':
                    return faArrowPointer;
                default:
                    return faArrowPointer;
            }
        }, [selectedOption]);

        return (
            <div className="flex size-10 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 shadow-sm">
                <FontAwesomeIcon
                    icon={icon}
                    className={twMerge('text-brand-violet-primary', className)}
                    ref={forwardedRef}
                    {...rest}
                />
            </div>
        );
    },
);

const MetricCardDropdown = () => {
    const { messageTypes, selectedOption, setSelectedOption } = useContext(MetricCardContext);

    const showEmailMetrics = messageTypes.includes(WorkflowActionTypeEnum.SEND_EMAIL);
    const showSmsMetrics = messageTypes.includes(WorkflowActionTypeEnum.SEND_SMS);
    const showClickThroughRate = showEmailMetrics || showSmsMetrics;

    return (
        <SingleSelect.Root value={selectedOption} onValueChange={setSelectedOption}>
            <SingleSelect.Trigger
                aria-label="Select a metric"
                size="sm"
                className="radix-state-open:ring-0 justify-start px-2 font-medium ring-0 focus:ring-0"
            >
                <SingleSelect.Value placeholder="Select metric" />
            </SingleSelect.Trigger>
            <SingleSelect.Content>
                <SingleSelect.Item value="total_revenue_from_messages">
                    <SingleSelect.ItemText>Total raised from messages</SingleSelect.ItemText>
                </SingleSelect.Item>
                <SingleSelect.Item value="average_revenue">
                    <SingleSelect.ItemText>Average revenue</SingleSelect.ItemText>
                </SingleSelect.Item>
                <SingleSelect.Item value="conversion_rate">
                    <SingleSelect.ItemText>Conversion rate</SingleSelect.ItemText>
                </SingleSelect.Item>
                <SingleSelect.Item value="unsubscribe_rate">
                    <SingleSelect.ItemText>Unsubscribe rate</SingleSelect.ItemText>
                </SingleSelect.Item>
                {showEmailMetrics && (
                    <>
                        <SingleSelect.Item value="email_bounce_rate">
                            <SingleSelect.ItemText>Bounce rate</SingleSelect.ItemText>
                        </SingleSelect.Item>
                        <SingleSelect.Item value="email_bounce_rate">
                            <SingleSelect.ItemText>Bounce rate</SingleSelect.ItemText>
                        </SingleSelect.Item>
                    </>
                )}
                {showSmsMetrics && (
                    <SingleSelect.Item value="sms_delivery_rate">
                        <SingleSelect.ItemText>Delivery rate</SingleSelect.ItemText>
                    </SingleSelect.Item>
                )}
                {showClickThroughRate && (
                    <SingleSelect.Item value="click_through_rate">
                        <SingleSelect.ItemText>Click-through rate</SingleSelect.ItemText>
                    </SingleSelect.Item>
                )}
            </SingleSelect.Content>
        </SingleSelect.Root>
    );
};

interface MetricCardContentProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {}
const MetricCardContent = forwardRef(
    (
        { className, ...rest }: MetricCardContentProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div className={twMerge('flex items-center gap-2')} ref={forwardedRef} {...rest}>
                <MetricCardData />
            </div>
        );
    },
);

const MetricCardData = () => {
    const { accountId } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();

    const { selectedOption } = useContext(MetricCardContext);
    const { data } = useSuspenseQuery({
        queryKey: workflowQueryKeys.workflowMetricsById(accountId, workflow.id),
        queryFn: () => fetchWorkflowMetrics(accountId, workflow.id),
    });

    switch (selectedOption) {
        case 'total_revenue_from_messages':
            return (
                <p className="text-2xl font-medium text-gray-900">
                    <CurrencySymbol />
                    {maskDecimal(data.total.revenue)}
                </p>
            );
        case 'average_revenue':
            return (
                <p className="text-2xl font-medium text-gray-900">
                    <CurrencySymbol />
                    {maskDecimal(data.total.average_revenue)}
                </p>
            );
        case 'conversion_rate':
            return (
                <p className="text-2xl font-medium text-gray-900">{data.total.conversion_rate}%</p>
            );
        case 'unsubscribe_rate':
            return (
                <div className="flex items-center gap-2.5">
                    <p className="text-2xl font-medium text-gray-900">
                        {data.total.unsubscribe_rate}%
                    </p>
                    <div className="h-6 w-0 border-r border-gray-200" />
                    <p className="text-lg text-gray-500">
                        {data.total.unsubscribe_count} unsubscribed
                    </p>
                </div>
            );
        case 'email_bounce_rate':
            return (
                <div className="flex items-center gap-2.5">
                    <p className="text-2xl font-medium text-gray-900">{data.email.bounce_rate}%</p>
                    <div className="h-6 w-0 border-r border-gray-200" />
                    <p className="text-lg text-gray-500">{data.email.bounce_count} bounced</p>
                </div>
            );
        case 'email_unique_open_rate':
            return (
                <div className="flex items-center gap-2.5">
                    <p className="text-2xl font-medium text-gray-900">{data.email.open_rate}%</p>
                    <div className="h-6 w-0 border-r border-gray-200" />
                    <p className="text-lg text-gray-500">{data.email.unique_open_count} opened</p>
                </div>
            );
        case 'sms_delivery_rate':
            return (
                <div className="flex items-center gap-2.5">
                    <p className="text-2xl font-medium text-gray-900">{data.sms.delivery_rate}%</p>
                    <div className="h-6 w-0 border-r border-gray-200" />
                    <p className="text-lg text-gray-500">{data.sms.delivery_count} delivered</p>
                </div>
            );
        case 'click_through_rate':
            return (
                <div className="flex items-center gap-2.5">
                    <p className="text-2xl font-medium text-gray-900">
                        {data.total.click_through_rate}%
                    </p>
                    <div className="h-6 w-0 border-r border-gray-200" />
                    <p className="text-lg text-gray-500">
                        {data.email.click_count + data.sms.click_count} clicks
                    </p>
                </div>
            );
        default:
            return null;
    }
};

const Root = MetricCardRoot;
const Header = MetricCardHeader;
const Icon = MetricCardIcon;
const Dropdown = MetricCardDropdown;
const Content = MetricCardContent;

export {
    Root,
    Header,
    Icon,
    Dropdown,
    Content,
    //
    MetricCardRoot,
    MetricCardHeader,
    MetricCardIcon,
    MetricCardDropdown,
    MetricCardContent,
};
